import React, { useState } from 'react';
import { Button, Checkbox, Header, Icon, List, Segment } from 'semantic-ui-react';
import { DUPLICATE_OPTIONS } from 'pages/DataMapping/DYLFieldsConstants';
import TierLabel from '../subcomponents/TierLabel';
import { RECOMMENDATION_TIERS, getOptionText } from '../constants';
import { EMAIL_PROVIDER_OPTIONS, IMPORT_MODULE_OPTIONS } from './importUtils';

const ConfirmImportIntegrations = ({ importData, integrations, isApprovedInitially = false, onBack, onConfirm }) => {
    const [isApproved, setIsApproved] = useState(isApprovedInitially);
    const mappedCount = (importData.mappings || []).filter(({ dyl_field }) => dyl_field).length;
    const skippedCount = (importData.mappings || []).length - mappedCount;
    const { analysis } = importData;
    const { email_provider, sync_calendar, web_form, lead_vendors, email_parsing } = integrations;
    const hasEmailIntegration = ['google', 'outlook'].includes(email_provider);

    return (
        <Segment>
            <Header as='h3'>
                Confirm Import & Integrations
                <Header.Subheader>Saved as a draft. The import runs and integrations are set up at launch.</Header.Subheader>
            </Header>
            <List divided relaxed>
                <List.Item>
                    <List.Icon name='upload' color='blue' />
                    <List.Content>
                        <List.Header>Data Import</List.Header>
                        <List.Description>
                            {importData.mode === 'file' ? (
                                <>
                                    {importData.file_name}: {importData.row_count} rows as {getOptionText(IMPORT_MODULE_OPTIONS, importData.module).toLowerCase()}.
                                    {' '}{mappedCount} columns matched{skippedCount ? `, ${skippedCount} not imported` : ''}.
                                    {' '}Duplicates: {getOptionText(DUPLICATE_OPTIONS, importData.duplicate_option).toLowerCase()}.
                                    {analysis && (analysis.duplicate_row_count || analysis.invalid_emails || analysis.invalid_phones) ? (
                                        <> Found {analysis.duplicate_row_count} possible duplicate(s), {analysis.invalid_emails} invalid email(s) and {analysis.invalid_phones} invalid phone(s).</>
                                    ) : null}
                                </>
                            ) : 'No import. You can import later from Settings → Integrations → Import Data.'}
                        </List.Description>
                    </List.Content>
                </List.Item>
                <List.Item>
                    <List.Icon name='mail outline' color='blue' />
                    <List.Content>
                        <List.Header>Email & Calendar {hasEmailIntegration && <TierLabel tier={RECOMMENDATION_TIERS.REQUIRES_ACTIVATION} size='mini' />}</List.Header>
                        <List.Description>
                            {hasEmailIntegration
                                ? `${getOptionText(EMAIL_PROVIDER_OPTIONS, email_provider)}${sync_calendar ? ' with calendar sync' : ''}. Each user connects their account after launch.`
                                : 'Not connecting now.'}
                        </List.Description>
                    </List.Content>
                </List.Item>
                <List.Item>
                    <List.Icon name='wpforms' color='blue' />
                    <List.Content>
                        <List.Header>Website Contact Form</List.Header>
                        <List.Description>{web_form.enabled ? web_form.name : 'Not adding one now.'}</List.Description>
                    </List.Content>
                </List.Item>
                <List.Item>
                    <List.Icon name='handshake outline' color='blue' />
                    <List.Content>
                        <List.Header>Lead Vendors</List.Header>
                        <List.Description>{lead_vendors.enabled ? lead_vendors.names.join(', ') : 'None.'}</List.Description>
                    </List.Content>
                </List.Item>
                <List.Item>
                    <List.Icon name='envelope open outline' color='blue' />
                    <List.Content>
                        <List.Header>Leads Sent by Email</List.Header>
                        <List.Description>{email_parsing.enabled ? 'Set up email lead parsing after launch.' : 'Not needed.'}</List.Description>
                    </List.Content>
                </List.Item>
            </List>
            <Segment secondary>
                <Checkbox
                    checked={isApproved}
                    onChange={(_, { checked }) => setIsApproved(checked)}
                    label="I've reviewed my import and integrations and approve them as my configuration draft"
                />
            </Segment>
            <Button basic type='button' onClick={onBack}><Icon name='arrow left' /> Back</Button>
            <Button primary floated='right' type='button' disabled={!isApproved} onClick={onConfirm}>
                Continue to Review & Launch <Icon name='arrow right' />
            </Button>
            <div style={{ clear: 'both' }} />
        </Segment>
    );
}

export default ConfirmImportIntegrations;
